import React, { useEffect } from "react";
import { Form, Input, Button, Select, message } from "antd";
import { useMutation, gql } from "@apollo/client";

const { Option } = Select;

// GraphQL mutations
const CREATE_USER_MUTATION = gql`
  mutation CreateUser(
    $id: ID
    $name: String!
    $email: String!
    $role: String!
    $contact: String
    $alertMethod: String
    $active: Int
    $conditions: [ConditionInput!]
  ) {
    createUser(
      id: $id
      name: $name
      email: $email
      role: $role
      contact: $contact
      alertMethod: $alertMethod
      active: $active
      conditions: $conditions
    ) {
      success
      message
      user { id name email }
    }
  }
`;

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser(
    $_id: ID!         # MongoDB _id required
    $id: ID           # NPI optional
    $name: String
    $email: String
    $role: String
    $contact: String
    $alertMethod: String
    $active: Int
    $conditions: [ConditionInput!]
  ) {
    updateUser(
      _id: $_id
      id: $id
      name: $name
      email: $email
      role: $role
      contact: $contact
      alertMethod: $alertMethod
      active: $active
      conditions: $conditions
    ) {
      success
      message
      user { id name email role contact alertMethod active }
    }
  }
`;

interface AddUserProps {
  onClose: () => void;
  editUserData?: any;
  refreshData?: () => void;
}

const AddUser: React.FC<AddUserProps> = ({ onClose, editUserData, refreshData }) => {
  const [form] = Form.useForm();
  const [createUser] = useMutation(CREATE_USER_MUTATION);
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);

  // Prefill form if editing
  useEffect(() => {
    if (editUserData) {
      let firstName = "";
      let lastName = "";

      if (editUserData.name) {
        const parts = editUserData.name.split(",");
        firstName = parts[0]?.trim() || "";
        lastName = parts[1]?.trim() || "";
      }

      form.setFieldsValue({
        email: editUserData.email ?? "",
        "first-name": firstName,
        "last-name": lastName,
        cellphone: editUserData.contact ?? "",
        NPI: editUserData.NPI ?? "",
        role: editUserData.role ?? "",
        "alert-method": editUserData.alertMethod ?? "",
        status: editUserData.active === 1 ? "true" : "false",
        conditions:
          editUserData.conditions?.map((c: any) => c.name).join(", ") ?? "",
      });
    } else {
      form.resetFields();
    }
  }, [editUserData, form]);

  const onFinish = async (values: any) => {
    try {
      if (editUserData) {
        // UPDATE
        const { data } = await updateUser({
          variables: {
            _id: editUserData._id,
            id: values.NPI || null,
            name: `${values["first-name"]}, ${values["last-name"]}`,
            email: values.email,
            role: values.role,
            contact: values.cellphone,
            alertMethod: values["alert-method"],
            active: values.status === "true" ? 1 : 0,
            conditions: values.conditions
              ? values.conditions.split(",").map((c: string) => ({ name: c.trim() }))
              : undefined,
          },
        });

        if (data?.updateUser?.success) {
          message.success("User updated successfully");
          refreshData?.();
          onClose(); // ✅ only on success
        } else {
          message.error(data?.updateUser?.message || "Update failed");
          return; // ✅ stay on page
        }
      } else {
        // CREATE
        const { data } = await createUser({
          variables: {
            id: values.NPI,
            name: `${values["first-name"]}, ${values["last-name"]}`,
            email: values.email,
            role: values.role,
            contact: values.cellphone,
            alertMethod: values["alert-method"],
            active: values.status === "true" ? 1 : 0,
            conditions: values.conditions
              ? values.conditions.split(",").map((c: string) => ({ name: c.trim() }))
              : [],
          },
        });

        if (data?.createUser?.success) {
          message.success("User added successfully");
          refreshData?.();
          onClose(); // ✅ only on success
        } else {
          message.error(data?.createUser?.message || "Create failed");
          return;
        }
      }
    } catch (err: any) {
      console.error(err);
      message.error(err.message || "Something went wrong");
      // ✅ do NOT close
    }
  };
  // Format raw 10-digit number as (999) 999-9999
  const formatPhoneNumber = (value?: string) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const len = digits.length;

    if (len < 4) return `(${digits}`;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  

  return (
    <div>
      <h2>{editUserData ? "Edit User" : "Add User"}</h2>
      <button onClick={onClose}>Back to Users</button>

      <Form
        form={form}
        name="userForm"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item label="Email / User ID" name="email" rules={[{ required: true, type: "email" }]}>
          <Input />
        </Form.Item>

        <Form.Item label="First Name" name="first-name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item label="Last Name" name="last-name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item
          label="Cell Phone"
          name="cellphone"
          rules={[
            { required: true, message: "Cell phone number is required" },
            {
              validator: (_, value) =>
                value && value.length === 10
                  ? Promise.resolve()
                  : Promise.reject("Cell phone must be 10 digits"),
            },
          ]}
          getValueProps={(value) => ({
            value: formatPhoneNumber(value),
          })}
          getValueFromEvent={(e) =>
            e.target.value.replace(/\D/g, "").slice(0, 10)
          }
        >
          <Input addonBefore="+1" placeholder="(123) 456-7890" />
        </Form.Item>
        

        <Form.Item label="Role" name="role" rules={[{ required: true }]}>
          <Select placeholder="Select role">
            <Option value="physician">Physician</Option>
            <Option value="care">Care</Option>
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          dependencies={["role"]}
        >
          {({ getFieldValue }) =>
            getFieldValue("role") === "physician" ? (
              <Form.Item
                label="NPI"
                name="NPI"
                rules={[
                  { required: true, message: "NPI is required" },
                  { pattern: /^\d{10}$/, message: "NPI must be exactly 10 digits" },
                ]}
              >
                <Input
                  maxLength={10}
                  inputMode="numeric"
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) e.preventDefault();
                  }}
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item label="Alert Method" name="alert-method" rules={[{ required: true }]}>
          <Select placeholder="Select alert method">
            <Option value="physician">Physician</Option>
            <Option value="physician-care">Physician Care</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Active" name="status" rules={[{ required: true }]}>
          <Select placeholder="Select status">
            <Option value="true">Yes</Option>
            <Option value="false">No</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Conditions" name="conditions">
          <Input placeholder="Comma-separated conditions" />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            {editUserData ? "Update User" : "Add User"}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddUser;